import { Button } from '@1trading/react/components/ui/button';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@1trading/react/components/ui/form';
import { Input } from '@1trading/react/components/ui/input';
import { Loading } from '@1trading/react/components/ui/loading';
import { useToast } from '@1trading/react/hooks/use-toast';
import { standardSchemaResolver } from '@hookform/resolvers/standard-schema';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Navigate, useNavigate } from 'react-router-dom';

import LoadingCard from '@/components/ui/loading-card';
import { PageCard, PageCardContent, PageCardFooter } from '@/components/ui/page-card';
import { PageHeading, PageHeadingDescription, PageHeadingTitle } from '@/components/ui/page-heading';
import {
  type InstitutionFuturesCompanyInformation,
  type InstitutionFuturesCompanyInformationOrDefaults,
  institutionFuturesCompanyInformationSchema,
} from '@/features/institution/futures/company-information/schema';
import { useProfileInstitutionDetailsAnswers, useProfileInstitutionDetailsMutation } from '@/hooks/queries/use-profile-institution-details';

const InstitutionFuturesCompanyInformationFormForm = ({ defaultValues }: { defaultValues: InstitutionFuturesCompanyInformationOrDefaults }) => {
  const form = useForm<InstitutionFuturesCompanyInformation>({
    defaultValues: {
      ...defaultValues,
      leiNumber: defaultValues.leiNumber ?? '',
    },
    resolver: standardSchemaResolver(institutionFuturesCompanyInformationSchema),
  });
  const { isPending, mutate } = useProfileInstitutionDetailsMutation();
  const navigate = useNavigate();
  const { toast } = useToast();
  const { t } = useTranslation();

  const onSubmit = (data: InstitutionFuturesCompanyInformation) => {
    mutate(
      { company: data },
      {
        onError: () => {
          toast({
            description: t('fundamentals.defaultError.message'),
            variant: 'destructive',
          });
        },
        onSuccess: () => {
          navigate('/institution/futures/services-agreement', { replace: true });
        },
      },
    );
  };

  return (
    <Form {...form}>
      <form name="institution-incorporation" onSubmit={form.handleSubmit(onSubmit)}>
        <PageCardContent className="pt-8 space-y-6">
          <FormField
            control={form.control}
            name="leiNumber"
            render={({ field }) => (
              <FormItem className="col-span-2">
                <FormLabel>{t('institutionDetails.company.leiNumber.title')}</FormLabel>
                <FormControl>
                  <Input {...field} placeholder={t('institutionDetails.company.placeholders.leiNumber')} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </PageCardContent>
        <PageCardFooter>
          <Button className="relative w-full" size="lg" type="submit" variant="primary" disabled={isPending}>
            <span className={isPending ? 'invisible' : ''}>{t('buttons.next')}</span>
            {isPending ? <Loading variant="inverted" position="overlay" size="sm" /> : null}
          </Button>
        </PageCardFooter>
      </form>
    </Form>
  );
};
InstitutionFuturesCompanyInformationFormForm.displayName = 'InstitutionFuturesCompanyInformationFormForm';

const InstitutionFuturesCompanyInformationForm = () => {
  const { isLoading, data: institutionDetailsAnswers } = useProfileInstitutionDetailsAnswers();
  const { t } = useTranslation();

  if (isLoading) return <LoadingCard />;

  const leiNumber = institutionDetailsAnswers?.company?.leiNumber;

  if (leiNumber) return <Navigate to="/institution/futures/decision-maker" replace />;

  const defaultValues = { leiNumber };

  return (
    <>
      <PageHeading>
        <PageHeadingTitle>{t('institutionDetails.company.title')}</PageHeadingTitle>
        <PageHeadingDescription>{t('institutionDetails.company.description')}</PageHeadingDescription>
      </PageHeading>
      <PageCard size="lg" bordered fixedFooter>
        <InstitutionFuturesCompanyInformationFormForm defaultValues={defaultValues} />
      </PageCard>
    </>
  );
};
InstitutionFuturesCompanyInformationForm.displayName = 'InstitutionFuturesCompanyInformationForm';

export default InstitutionFuturesCompanyInformationForm;
